import { useMemo } from 'react';
import { graphql } from 'react-relay';
import type { Person } from '@atlassian/jira-filters/src/common/types.tsx';
import { useFetchQuery } from '@atlassian/jira-relay-utils/src/services/use-fetch-query/index.tsx';
import type {
	getCurrentUserRelayQuery$variables,
	getCurrentUserRelayQuery$data,
} from '@atlassian/jira-relay/src/__generated__/getCurrentUserRelayQuery.graphql';
import { toAccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';

// @ts-expect-error - TS7006 - Parameter 'user' implicitly has an 'any' type.
const transformUser = (user): Person | Error => {
	const { accountId, picture, name } = user;
	if (accountId == null || name == null) {
		return Error('Invalid user returned');
	}

	return {
		id: toAccountId(accountId),
		displayName: name,
		avatarUrl: picture,
	};
};

// @ExportForTesting
export const transformResult = ({
	loading,
	error,
	data,
}: {
	loading: boolean;
	error: Error | null;
	data: getCurrentUserRelayQuery$data | null;
}): Person | null => {
	if (loading || error) {
		return null;
	}

	if (data == null || data.user == null) {
		return null;
	}

	const transformedResult = transformUser(data.user);

	return transformedResult instanceof Error ? null : transformedResult;
};

export const useLoggedInUser = (): Person | null => {
	const accountId = useAccountId();
	const queryResult = useFetchQuery<
		getCurrentUserRelayQuery$variables,
		getCurrentUserRelayQuery$data,
		undefined
	>(
		graphql`
			query getCurrentUserRelayQuery($accountId: ID!) {
				user(accountId: $accountId) {
					accountId
					picture
					name
				}
			}
		`,
		{ accountId: accountId ?? '' },
		{ skip: accountId === null, fetchPolicy: 'store-or-network' },
	);

	const userQueryResult = useMemo(() => transformResult(queryResult), [queryResult]);
	return userQueryResult;
};
